import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import type { IztContentNode } from '../api/types';
import type { RailPanelId } from '../layout/LeftRail';
import { railPanelTitle } from '../layout/LeftRail';
import { ChevronLeftIcon, ClipboardCheckIcon, LinkIcon, PanelCollapseIcon, UploadIcon } from '../layout/icons';
import { PaneScrim } from '../layout/PaneScrim';
import type { BoardContextValue, BoardNavigationState } from './useBoardContext';
import { LectureLibraryPanel } from './panels/LectureLibraryPanel';
import type { LectureLibrarySelection } from './panels/LectureLibraryTree';
import { LectureDropZone } from './panels/LectureDropZone';
import { UseLectureConfirm } from './panels/UseLectureConfirm';
import { InsertLinkPanel, type InsertedYoutubeVideo } from './panels/InsertLinkPanel';
import { EmptyState } from '../components';
import { contentMetaLine, stripLeadingOrdinal, type ContentFilter } from '../config/content-types';
import { featureEnabled } from '../config/features';
import { getStudentEmailDomain } from '../api/environment';
import { UploadPanel } from './panels/UploadPanel';
import type { QltUploadFile } from '../api/repository/upload';
import { LibrarySections, type LibrarySection } from './panels/LibrarySections';
import { SubmissionsPanel } from './submissions/SubmissionsPanel';
import { ExamsPanel } from './exams/ExamsPanel';
import { STUDENT_EMAIL_DOMAIN_UNSET, type SubmissionTask } from './submissions/submissions';
import { resolvePeriodCourseScope } from './periodLabels';
import { useQltTimetableStore } from '../stores/qlt';
import { BoardLectureSuggestions } from './BoardLectureSuggestions';
import { activeSession } from './results/sessions';
import { useResultSessionStore } from './results/store';
import type { UnitSelection } from './useSelectUnit';

/** The export zero-pads the chapter count and leaves the lesson count plain. */
function courseCounts(course: IztContentNode): string {
  let units = 0;
  course.sections.forEach((section) => { units += section.units.length; });
  return contentMetaLine([`${String(course.sections.length).padStart(2, '0')} chương`, `${units} bài`]);
}

export interface RailPanelHostProps {
  activeId: RailPanelId;
  context: BoardContextValue;
  videos: InsertedYoutubeVideo[];
  libraryFilter?: ContentFilter;
  onInsertVideo: (video: InsertedYoutubeVideo) => void;
  onPlayVideo: (video: InsertedYoutubeVideo) => void;
  onOpenUpload: (file: QltUploadFile) => void;
  /** A bài tập row: its đề opens as a tab with the class's results beside it. */
  onOpenTask: (task: SubmissionTask, courseTitle: string) => void;
  onOpenExam: (examId: string) => void;
  onSelectSuggestion: (selection: UnitSelection) => void;
  onClose: () => void;
}

export function RailPanelHost({ activeId, context, videos, libraryFilter, onInsertVideo, onPlayVideo, onOpenUpload, onOpenTask, onOpenExam, onSelectSuggestion, onClose }: RailPanelHostProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const cells = useQltTimetableStore((state) => state.todayCells);
  // a board opened by URL has no route state; Kho bài and the exam lookup still need the tree's grade and subject
  const courseScope = resolvePeriodCourseScope({ gradeId: context.gradeId, subjectId: context.subjectId }, cells, context);
  const [selection, setSelection] = useState<LectureLibrarySelection | null>(null);
  // course detail replaces the panel title, so the host owns it rather than the library body
  const [course, setCourse] = useState<IztContentNode | null>(null);
  // PO addition - a row being dragged arms the canvas drop ring
  const [dragged, setDragged] = useState<LectureLibrarySelection | null>(null);
  const [section, setSection] = useState<LibrarySection>('lectures');
  const activeTab = useResultSessionStore((state) => activeSession(state.sessions, context.unitId));

  useEffect(() => {
    if (activeId !== 'library') { setCourse(null); setDragged(null); }
  }, [activeId]);

  // a filtered open (the AI import) is about material, so it lands on Học liệu
  useEffect(() => { if (libraryFilter) setSection('lectures'); }, [libraryFilter]);

  function useLecture(next: LectureLibrarySelection) {
    const search = new URLSearchParams(location.search);
    search.set('unit', next.unit.id);
    const state = (location.state || {}) as BoardNavigationState;
    navigate({ pathname: location.pathname, search: `?${search.toString()}` }, { replace: true, state: { ...state, lessonTitle: stripLeadingOrdinal(next.unit.name), courseId: next.courseId } });
    setSelection(null);
    setDragged(null);
  }

  const dock = <button type="button" className="libpanel__dock" aria-label="Ẩn bảng công cụ" onClick={onClose}><PanelCollapseIcon size={20} /></button>;

  return (
    <>
      {/* the rail stays above it, so Kho bài -> Tải lên still switches without closing first */}
      <PaneScrim onDismiss={onClose} />
      <section className="rail-panel libpanel">
        {course ? (
          <header className="libpanel__head libpanel__head--course">
            <div className="libpanel__crumb">
              <button type="button" className="libpanel__back" aria-label="Quay lại kho bài" onClick={() => setCourse(null)}><ChevronLeftIcon size={18} /></button>
              <h2 title={course.name}>Khoá học: {course.name}</h2>
            </div>
            <p className="libpanel__crumb-meta">{courseCounts(course)}</p>
          </header>
        ) : (
          <header className="libpanel__head">
            <h2>{railPanelTitle(activeId)}</h2>
            {dock}
          </header>
        )}
        <div className="libpanel__body">
          {activeId === 'library' && course ? <LectureLibraryPanel classId={context.classId} gradeId={courseScope.gradeId} subjectId={courseScope.subjectId} activeUnitId={context.unitId} openCourse={course} onOpenCourse={setCourse} onRequestLecture={setSelection} onDragLecture={setDragged} initialFilter={libraryFilter} /> : null}
          {activeId === 'library' && !course ? (
            <LibrarySections value={section} onChange={setSection}>
              {section === 'lectures' ? <LectureLibraryPanel classId={context.classId} gradeId={courseScope.gradeId} subjectId={courseScope.subjectId} activeUnitId={context.unitId} openCourse={null} onOpenCourse={setCourse} onRequestLecture={setSelection} onDragLecture={setDragged} initialFilter={libraryFilter} /> : null}
              {section === 'homework' ? (getStudentEmailDomain()
                ? <SubmissionsPanel classTitle={context.classTitle} onOpen={onOpenTask} activeTaskId={activeTab ? activeTab.refId : null} />
                : <EmptyState icon={<ClipboardCheckIcon size={26} />} title="Bài nộp chưa được cấu hình" description={STUDENT_EMAIL_DOMAIN_UNSET} />) : null}
              {section === 'exams' ? <ExamsPanel classTitle={context.classTitle} classId={context.classId} gradeId={courseScope.gradeId} subjectId={courseScope.subjectId} onOpen={onOpenExam} activeExamId={activeTab ? activeTab.refId : null} /> : null}
            </LibrarySections>
          ) : null}
          {activeId === 'suggestions' ? <BoardLectureSuggestions context={context} onSelect={onSelectSuggestion} /> : null}
          {activeId === 'upload' ? (featureEnabled('board.upload')
            ? <UploadPanel onOpenUpload={onOpenUpload} />
            : <EmptyState icon={<UploadIcon size={26} />} title="Tải lên đang được tắt" description="Quản trị viên có thể bật tính năng sau khi kiểm tra giới hạn tệp của nhà trường." />) : null}
          {activeId === 'link' ? (featureEnabled('board.link')
            ? <InsertLinkPanel videos={videos} onInsert={onInsertVideo} onPlay={onPlayVideo} />
            : <EmptyState icon={<LinkIcon size={26} />} title="Chèn liên kết đang được tắt" description="Quản trị viên có thể bật tính năng trong trang thiết lập." />) : null}
        </div>
      </section>
      {dragged ? <LectureDropZone label={stripLeadingOrdinal(dragged.unit.name)} onDrop={() => useLecture(dragged)} /> : null}
      <UseLectureConfirm selection={selection} onClose={() => setSelection(null)} onConfirm={useLecture} />
    </>
  );
}
